import { ErrorMessage } from 'src/model/error-message';
import { useAlertDialog } from '@zarafront/lib-zds';
import { i18n } from 'i18next';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Contains information about how to display an error
 */
export interface ErrorNotification {
    /**
     * The error message and (optional) title keys.
     * Properties of the form 'error.<messageKey>' for the message an 'error.title.<titleKey>' will be used, if available. 
     * If not, messageKey and/or titleKey will be used directly
     */
    error: ErrorMessage;
    /**
     * The label of the 'accept' button. By the default, 'OK'.
     */
    acceptButtonText?: string;
    /**
     * The label of the 'accept' button. By the default, 'OK'.
     */
    cancelButtonText?: string;
    /**
     * Whether to show the cancel button or not
     */
    showCancel?: boolean;
    /**
     * A callback to execute when the accept button is clicked
     */
    onAcceptClick?: () => void;
    /**
     * a callback to execute when the cancel button is clicked (or esc key is pressed)
     */
    onCancelClick?: () => void;
}

export type ErrorNotifier = (errorInfo: ErrorNotification) => void;

const getPropertyValue = (key: string, prefix: string, i18n: i18n) => {
    const keys = [key, `${prefix}${key}`];
    const _key = keys.find(key => i18n.exists(key));
    return _key ? i18n.t(_key) : key;
}

const ShowErrorsContext = React.createContext<ErrorNotifier>((_) => {});

export const useShowError = () => {
    return useContext(ShowErrorsContext);
}

export default (props: { children: React.ReactNode }) => {
    const { t, i18n } = useTranslation();
    const { alert } = useAlertDialog();
    const showError = (errorInfo: ErrorNotification) => {
        const { titleKey, messageKey } = errorInfo.error;
        const title = titleKey ? getPropertyValue(titleKey, 'errors.title.', i18n) : '';
        const message = getPropertyValue(messageKey, 'errors.', i18n);
        alert({
            title,
            description: message,
            acceptLabel: errorInfo.acceptButtonText ?? t('common_dialog_accept'),
            cancelLabel: errorInfo.cancelButtonText ? t(errorInfo.cancelButtonText) : (errorInfo.showCancel ? t('common_dialog_cancel') : undefined),
            onAccept: errorInfo.onAcceptClick,
            onCancel: errorInfo.onCancelClick
        });
    }

    return (
        <ShowErrorsContext.Provider value={showError}>
            { props.children }
        </ShowErrorsContext.Provider>
    )
}